/**
 * SCENE 05: THE ANVIL OF RHYTHMIC REFINEMENT (Industrial Era // 1780 AD)
 * 20 steady cadence hammer strikes transmute hot iron ore into a mirror of liquid metal.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';

export class Scene05_Anvil implements ISceneLifecycle {
  public id = 4;
  public name = 'The Anvil of Rhythmic Refinement';
  public era = 'Industrial Era (1780 AD)';

  private eventBus: EventBus;
  private store: StateStore;
  private strikeCount: number = 0;
  private readonly TARGET_STRIKES = 20;
  private isRefined: boolean = false;
  private sparkFlash: number = 0;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubStrike = this.eventBus.subscribe(EventType.INPUT_GESTURE_STRIKE, () => {
      if (!this.isRefined) {
        this.strikeCount++;
        this.sparkFlash = 1.0;
        this.eventBus.emit(EventType.AUDIO_TRIGGER_BELL);

        if (this.strikeCount >= this.TARGET_STRIKES) {
          this.isRefined = true;
          this.store.setFlag(StateBitmask.FORGE_REFINED, true);
          this.eventBus.emit(
            EventType.ACCESSIBILITY_ANNOUNCE,
            0, 0, 0, 0, 0, 1, 4,
            '20 hammer strikes complete! Raw iron ore transmutes into a mirror of liquid metal reflecting the open sky.'
          );
        }
      }
    });

    this.unsubscribeList.push(unsubStrike);
  }

  public update(dt: number): void {
    if (this.sparkFlash > 0) {
      this.sparkFlash = Math.max(0, this.sparkFlash - dt * 4.0);
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    if (this.isRefined) {
      // Liquid Metal Mirror surface reflecting open sky
      const mirrorGrad = ctx.createLinearGradient(0, cy - 100, 0, cy + 100);
      mirrorGrad.addColorStop(0, '#e8f4f8');
      mirrorGrad.addColorStop(0.5, '#c5dbe4');
      mirrorGrad.addColorStop(1, '#7e9eb2');

      ctx.fillStyle = mirrorGrad;
      ctx.beginPath();
      ctx.ellipse(cx, cy, 220, 110, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.font = '600 13px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#1c2d37';
      ctx.fillText('LIQUID METAL MIRROR  //  PERFECT REFINEMENT', cx, cy);
    } else {
      // Hot Iron Ore sitting on subterranean forge
      ctx.fillStyle = '#1b1411';
      ctx.fillRect(0, 0, width, height);

      // Anvil block
      ctx.fillStyle = '#332c28';
      ctx.fillRect(cx - 100, cy, 200, 120);

      // Hot Iron Glowing Ore
      const heatProgress = this.strikeCount / this.TARGET_STRIKES;
      const oreColor = `rgba(${Math.floor(226 + heatProgress * 29)}, ${Math.floor(91 + heatProgress * 120)}, 45, 0.95)`;
      
      ctx.shadowColor = 'rgba(240, 120, 40, 0.9)';
      ctx.shadowBlur = 20 + this.sparkFlash * 40;
      ctx.fillStyle = oreColor;
      ctx.fillRect(cx - 60, cy - 25, 120, 50);

      ctx.font = '600 12px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.fillText(`STRIKE ORE WITH CADENCE: ${this.strikeCount} / ${this.TARGET_STRIKES}`, cx, cy - 60);

      ctx.font = '400 11px monospace';
      ctx.fillStyle = 'rgba(220, 200, 180, 0.5)';
      ctx.fillText('[ TAP / STRIKE TO HAMMER ORE ]', cx, height - 40);
    }

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
